import { useEffect, useRef } from "react";

// Keep mounted Git views synchronized with terminal, agent, and other-panel edits.
export const gitChangedEvent = "workspace-git-changed";
export function useGitRefresh(refresh: () => Promise<unknown>, active = true) {
  const root = useRef<HTMLDivElement>(null);
  const current = useRef(refresh);
  current.current = refresh;
  useEffect(() => {
    const update = () => {
      if (
        document.visibilityState === "visible" &&
        root.current?.getClientRects().length
      )
        void current.current();
    };
    if (!active) return;
    let debounce: number | undefined;
    const onDataChange = () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(update, 300);
    };
    update();
    const timer = window.setInterval(update, 5000);
    window.addEventListener("lumen:data-changed", onDataChange);
    window.addEventListener("focus", update);
    window.addEventListener(gitChangedEvent, update);
    document.addEventListener("visibilitychange", update);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(debounce);
      window.removeEventListener("lumen:data-changed", onDataChange);
      window.removeEventListener("focus", update);
      window.removeEventListener(gitChangedEvent, update);
      document.removeEventListener("visibilitychange", update);
    };
  }, [active]);
  return root;
}

export function useGitChange(onChange: () => void) {
  const callback = useRef(onChange);
  callback.current = onChange;
  useEffect(() => {
    const update = () => callback.current();
    window.addEventListener(gitChangedEvent, update);
    return () => window.removeEventListener(gitChangedEvent, update);
  }, []);
}
